import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Lock, MailCheck } from "lucide-react";
import { publicApi, errMsg } from "@/lib/api";
import { toast } from "sonner";
import BrandLogo from "@/components/BrandLogo";

/**
 * /forgot-password
 *
 * Public, unauthenticated. Deliberately does NOT reveal whether the
 * submitted email address exists. Always shows the same neutral
 * confirmation panel so that this page cannot be used for account
 * enumeration.
 */
export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setBusy(true);
    try {
      // publicApi is deliberately used so no stale JWT can leak onto
      // this public request path.
      await publicApi.post("/auth/forgot-password", { email });
      setSubmitted(true);
    } catch (err) {
      // Even on error, present the same neutral state so behavior is
      // uniform for account-enumeration resistance.
      setSubmitted(true);
      toast.error(errMsg(err, "Request submitted."));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      <div className="brand-strip text-white p-10 md:p-14 hidden md:flex flex-col justify-between relative overflow-hidden">
        <div className="pointer-events-none absolute -right-32 -bottom-32 opacity-[0.10]" aria-hidden="true">
          <BrandLogo variant="kandake-gold" size="2xl" />
        </div>
        <div className="relative flex items-center gap-3">
          <BrandLogo variant="gfmp" size="sm" withRing />
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] opacity-75">Operating authority</div>
            <div className="text-sm font-medium leading-tight">Good Friar Music Publishing</div>
            <div className="text-[10px] uppercase tracking-[0.18em] opacity-65">The Durban 49 Group</div>
          </div>
        </div>
        <div className="relative flex flex-col items-start">
          <BrandLogo variant="kandake-gold" size="2xl" frame="square" className="-ml-2 mb-2" />
          <div className="font-display text-[44px] leading-[1.05] tracking-tight" style={{ color: "hsl(40 48% 80%)" }}>
            Kandake Prime
          </div>
          <div className="text-[11px] uppercase tracking-[0.22em] opacity-80 mt-1">
            Internal Alpha · GFMP Command Center
          </div>
          <p className="font-display text-xl leading-snug max-w-md mt-6 opacity-95">
            A private operating room for catalog readiness, compliance, and licensing workflow.
          </p>
        </div>
        <div className="relative">
          <p className="text-sm opacity-80 max-w-md">
            <span className="block font-medium text-white/95">GFMP / Durban 49 internal access only.</span>
            <span className="block mt-1">Compliance support platform. Not legal advice.</span>
          </p>
        </div>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-10 bg-paper">
        <Card className="w-full max-w-md shadow-sm">
          <CardContent className="p-8">
            <div className="flex items-center gap-2 mb-3">
              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[10px] uppercase tracking-[0.16em] font-medium"
                    style={{ background: "hsl(40 48% 92%)", color: "hsl(347 72% 22%)", border: "1px solid hsl(40 48% 78%)" }}
                    data-testid="forgot-internal-alpha-badge">
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: "hsl(40 48% 45%)" }} /> Internal Alpha
              </span>
              <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
                <Lock className="w-3 h-3" /> Internal use only
              </span>
            </div>
            <h1 className="font-display text-3xl text-foreground" data-testid="forgot-password-title">Reset your password</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Enter the work email associated with your Kandake account. If an eligible account exists,
              reset instructions will be issued.
            </p>

            {!submitted ? (
              <form onSubmit={handleSubmit} className="mt-6 space-y-4" data-testid="forgot-password-form">
                <div>
                  <Label htmlFor="forgot-email">Work email</Label>
                  <Input
                    id="forgot-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                    data-testid="forgot-password-email-input"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full"
                  disabled={busy}
                  data-testid="forgot-password-submit-button"
                  style={{ background: "hsl(347 72% 22%)" }}
                >
                  {busy ? "Submitting…" : "Send reset instructions"}
                </Button>
              </form>
            ) : (
              <div
                className="mt-6 rounded-lg border p-4 flex gap-3"
                style={{ background: "hsl(40 48% 96%)", borderColor: "hsl(40 48% 78%)" }}
                data-testid="forgot-password-neutral-response"
                role="status"
                aria-live="polite"
              >
                <MailCheck className="w-5 h-5 shrink-0 mt-0.5" style={{ color: "hsl(347 72% 22%)" }} />
                <div className="text-sm leading-relaxed">
                  If an eligible account exists for that email address, password-reset
                  instructions have been issued. Reset links expire after 30 minutes and
                  can be used only once. If you did not initiate this request, you may
                  safely ignore it.
                </div>
              </div>
            )}

            <div className="text-center mt-6 text-[12px]">
              <Link
                to="/login"
                className="text-muted-foreground hover:text-foreground underline"
                data-testid="forgot-password-back-to-login"
              >
                ← Back to sign in
              </Link>
            </div>
            <div className="text-[11px] text-muted-foreground mt-4 leading-relaxed text-center">
              Compliance support platform. Not legal advice. Human review required before external submission.
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
